import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation, languages } from '../../../i18n';
import { supabase } from '../../../lib/supabase';
import { getErrorMessage } from '../../../lib/errors';
import { useAuthStore } from '../../../stores/authStore';
import CardNB from '../../../components/ui-nb/CardNB';
import ButtonNB from '../../../components/ui-nb/ButtonNB';
import IconNB from '../../../components/ui-nb/IconNB';
import BadgeNB from '../../../components/ui-nb/BadgeNB';
import ChangePasswordModal from '../../../components/ChangePasswordModal';
import EquipmentTypeList from '../equipment-types/EquipmentTypeList';
import { error as logError } from '../../../lib/logger';

type Section = 'my-account' | 'subscription' | 'equipment-types' | 'verification' | 'notifications' | 'language' | 'logout';

interface SidebarItem {
  key: Section;
  label: string;
  icon: string;
  adminOnly?: boolean;
  href?: string; // external link instead of inline section
}

interface DeliveryVerificationSettings {
  method: 'otp' | 'id_photo' | 'collection_photo';
  enabled: boolean;
}

interface NotificationSettings {
  push: boolean;
  sms: boolean;
  whatsapp: boolean;
}

interface ProfileData {
  full_name: string;
  email: string;
  preferences: {
    language?: string;
    notifications?: NotificationSettings;
    verification?: DeliveryVerificationSettings;
  };
}

interface SubscriptionData {
  plan: string;
  status: string;
  current_period_end: string | null;
}

export default function ConfigHome() {
  const { t, language, setLanguage } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeSection = (searchParams.get('section') || 'my-account') as Section;
  const userId = useAuthStore(s => s.user?.id);
  const userRole = useAuthStore(s => s.user?.role);
  const userProvider = useAuthStore(s => s.user?.provider);
  const orgName = useAuthStore(s => s.user?.organizationName);
  const signOut = useAuthStore(s => s.signOut);
  const [loading, setLoading] = useState(true);

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const isAdmin = userRole === 'admin';
  const { lang: urlLang } = useParams<{ lang: string }>();
  const navLang = urlLang || 'en';

  const sidebarItems: SidebarItem[] = [
    { key: 'my-account', label: t('config.myAccount.heading') || 'My Account', icon: 'users' },
    { key: 'subscription', label: t('config.subscription.heading') || 'Subscription', icon: 'creditCard' },
    ...(isAdmin ? [
      { key: 'equipment-types' as Section, label: 'Equipment Types', icon: 'equipment', adminOnly: true },
    ] : []),
    { key: 'verification', label: t('config.verification.heading') || 'Verification', icon: 'shield' },
    { key: 'notifications', label: t('config.notifications.heading') || 'Notifications', icon: 'bell' },
    { key: 'language', label: t('config.language.heading') || 'Language', icon: 'globe' },
    { key: 'logout', label: t('config.logout.heading') || 'Logout', icon: 'close' },
  ];

  // Render sidebar items — equipment types renders inline, not navigate-away
  // (remove useEffect that was navigating to /admin/equipment-types)

  const [profile, setProfile] = useState<ProfileData>({
    full_name: '',
    email: '',
    preferences: {},
  });

  const [subscription, setSubscription] = useState<SubscriptionData>({
    plan: 'free',
    status: 'active',
    current_period_end: null,
  });

  const [deliveryVerification, setDeliveryVerification] = useState<DeliveryVerificationSettings>({
    method: 'otp',
    enabled: true,
  });

  const [notifications, setNotifications] = useState<NotificationSettings>({
    push: true,
    sms: false,
    whatsapp: true,
  });

  // Fetch profile and subscription data
  useEffect(() => {
    if (!userId) return;
    async function fetchData() {
      try {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('full_name, email, preferences')
          .eq('id', userId)
          .single();

        if (!profileError && profileData) {
          setProfile({
            full_name: profileData.full_name || '',
            email: profileData.email || '',
            preferences: profileData.preferences || {},
          });
          if (profileData.preferences?.notifications) {
            setNotifications(profileData.preferences.notifications);
          }
          if (profileData.preferences?.verification) {
            setDeliveryVerification(profileData.preferences.verification);
          }
        }

        const { data: subData } = await supabase
          .from('subscriptions')
          .select('plan, status, current_period_end')
          .eq('organization_id', useAuthStore.getState().user?.organizationId)
          .maybeSingle();

        if (subData) {
          setSubscription(subData);
        }
      } catch (err) {
        logError('Error fetching config data', { error: getErrorMessage(err) });
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [userId]);

  // Save preferences to DB
  const savePreferences = async (prefs: Partial<ProfileData['preferences']>) => {
    if (!userId) return;
    const merged = { ...profile.preferences, ...prefs };
    try {
      await supabase
        .from('profiles')
        .update({ preferences: merged })
        .eq('id', userId);
      setProfile(p => ({ ...p, preferences: merged }));
    } catch (err) {
      logError('Error saving preferences', { error: getErrorMessage(err) });
    }
  };

  const handleLanguageChange = async (lang: string) => {
    setLanguage(lang as 'en' | 'es' | 'fr');
    await savePreferences({ language: lang });
  };

  const handleNotificationToggle = async (key: keyof NotificationSettings) => {
    const updated = { ...notifications, [key]: !notifications[key] };
    setNotifications(updated);
    await savePreferences({ notifications: updated });
  };

  const handleVerificationChange = async (updates: Partial<DeliveryVerificationSettings>) => {
    const updated = { ...deliveryVerification, ...updates };
    setDeliveryVerification(updated);
    await savePreferences({ verification: updated });
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    const word = isAdmin ? (orgName || profile.full_name || profile.email || '') : (profile.full_name || profile.email || '');
    if (deleteConfirmText !== word) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      const { data, error } = await supabase.functions.invoke('delete-account');
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Deletion failed');
      await signOut();
      navigate('/');
    } catch (err) {
      logError('Delete account error', { error: getErrorMessage(err) });
      setDeleteError(getErrorMessage(err) || 'Failed to delete account');
    } finally {
      setDeleteLoading(false);
    }
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'equipment-types':
        return <EquipmentTypeList />;

      case 'my-account': {
        const word = isAdmin ? (orgName || profile.full_name || profile.email || '') : (profile.full_name || profile.email || '');
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--nb-text-primary)', marginBottom: '1rem' }}>{t('config.myAccount.heading')}</h2>
              <div style={{ background: 'var(--nb-bg-card)', border: '3px solid #000', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#787878', marginBottom: '0.25rem' }}>{t('config.myAccount.name')}</label>
                  <p style={{ color: '#000' }}>{profile.full_name || '—'}</p>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#787878', marginBottom: '0.25rem' }}>{t('config.myAccount.email')}</label>
                  <p style={{ color: '#000' }}>{profile.email || '—'}</p>
                </div>
                <div style={{ paddingTop: '1rem', borderTop: '2px solid #000' }}>
                  {userProvider === 'google' ? (
                    <p style={{ color: '#787878', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <IconNB name="settings" size={16} />
                      {t('config.myAccount.googleAuthNote')}
                    </p>
                  ) : (
                    <button
                      onClick={() => setShowPasswordModal(true)}
                      style={{ color: '#000000', fontWeight: 500, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      <IconNB name="settings" size={16} />
                      {t('config.myAccount.changePassword')}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Delete Account section */}
            <div>
              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'transparent',
                    border: '3px solid #D64545',
                    color: '#D64545',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    textAlign: 'center',
                  }}
                >
                  Delete Account
                </button>
              ) : (
                <div style={{ background: 'var(--nb-bg-page)', border: '2px solid #D64545', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <span style={{ fontSize: '1.25rem' }}>⚠️</span>
                    <div>
                      <p style={{ fontWeight: 600, color: '#D64545', marginBottom: '0.5rem' }}>
                        This action is permanent and cannot be undone.
                      </p>
                      {isAdmin ? (
                        <ul style={{ color: '#4A4A4A', fontSize: '0.875rem', lineHeight: 1.6, paddingLeft: '1.25rem', margin: 0 }}>
                          <li>Your organization <strong>"{orgName}"</strong> will be permanently deleted</li>
                          <li>All branches, orders, and data will be erased</li>
                          <li>All subscriptions will be cancelled</li>
                          <li>Other team members will lose access to the organization</li>
                          <li>Your account and profile will be permanently deleted</li>
                        </ul>
                      ) : (
                        <ul style={{ color: '#4A4A4A', fontSize: '0.875rem', lineHeight: 1.6, paddingLeft: '1.25rem', margin: 0 }}>
                          <li>Your profile and personal data will be permanently deleted</li>
                          <li>Your association with the organization will be removed</li>
                          <li>The organization and other members will not be affected</li>
                        </ul>
                      )}
                    </div>
                  </div>

                  <div style={{ borderTop: '2px solid #D64545', paddingTop: '1rem' }}>
                    <p style={{ color: '#4A4A4A', fontSize: '0.875rem', marginBottom: '0.75rem' }}>
                      To confirm, type <strong style={{ color: '#000' }}>"{word}"</strong> below:
                    </p>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder={word}
                      style={{
                        width: '100%',
                        padding: '0.5rem 0.75rem',
                        border: '2px solid #000',
                        background: 'var(--nb-bg-card)',
                        color: 'var(--nb-text-primary)',
                        fontSize: '0.875rem',
                        outline: 'none',
                      }}
                    />
                  </div>

                  {deleteError && (
                    <p style={{ color: '#D64545', fontSize: '0.875rem' }}>{deleteError}</p>
                  )}

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                    <ButtonNB
                      variant="primary"
                      onClick={handleDeleteAccount}
                      loading={deleteLoading}
                      disabled={deleteConfirmText !== word}
                      style={{
                        flex: 1,
                        background: deleteConfirmText === word ? '#dc2626' : '#9ca3af',
                        opacity: deleteConfirmText === word ? 1 : 0.5,
                      }}
                    >
                      Delete My Account Permanently
                    </ButtonNB>
                    <ButtonNB
                      variant="secondary"
                      onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); setDeleteError(null); }}
                    >
                      Cancel
                    </ButtonNB>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      }

      case 'subscription':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#000' }}>{t('config.subscription.heading')}</h2>
            <div style={{ background: 'var(--nb-bg-card)', border: '3px solid #000', padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                <div>
                  <p style={{ fontSize: '0.875rem', color: '#787878' }}>{t('config.subscription.currentPlan')}</p>
                  <p style={{ fontSize: '1.5rem', fontWeight: 700, textTransform: 'capitalize', color: '#000' }}>{subscription.plan}</p>
                </div>
                <BadgeNB variant="success">
                  {subscription.status}
                </BadgeNB>
              </div>
              {subscription.current_period_end && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <p style={{ fontSize: '0.875rem', color: '#787878' }}>{t('config.subscription.nextRenewal')}</p>
                  <p style={{ color: '#000' }}>{new Date(subscription.current_period_end).toLocaleDateString()}</p>
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <ButtonNB variant="primary" style={{ flex: 1 }}>
                  {t('config.subscription.upgrade')}
                </ButtonNB>
                <ButtonNB variant="secondary" style={{ flex: 1 }}>
                  {t('config.subscription.renew')}
                </ButtonNB>
              </div>
            </div>
          </div>
        );

      case 'verification':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#000' }}>{t('config.verification.heading')}</h2>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <span style={{ fontSize: '0.875rem', color: '#787878' }}>{t('config.verification.enabled')}</span>
                <button
                  type="button"
                  onClick={() => handleVerificationChange({ enabled: !deliveryVerification.enabled })}
                  style={{
                    position: 'relative',
                    width: '2.75rem',
                    height: '1.5rem',
                    background: deliveryVerification.enabled ? '#000000' : '#d1d5db',
                    border: '2px solid #000',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{
                    position: 'absolute',
                    top: '0.1rem',
                    left: deliveryVerification.enabled ? '1.3rem' : '0.1rem',
                    width: '1rem',
                    height: '1rem',
                    background: 'var(--nb-bg-card)',
                    border: '2px solid #000',
                    transition: 'transform 0.15s',
                  }} />
                </button>
              </label>
            </div>
            <div style={{ background: 'var(--nb-bg-card)', border: '3px solid #000', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <p style={{ fontSize: '0.875rem', color: '#787878' }}>{t('config.verification.verificationMethod')}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[
                  { value: 'otp', label: t('config.verification.otpSmsWhatsApp'), desc: t('config.verification.otpSmsWhatsAppDesc') },
                  { value: 'id_photo', label: t('config.verification.idPhoto'), desc: t('config.verification.idPhotoDesc') },
                  { value: 'collection_photo', label: t('config.verification.collectionPhoto'), desc: t('config.verification.collectionPhotoDesc') },
                ].map((option) => (
                  <label key={option.value} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.75rem', background: 'var(--nb-bg-card)', border: '2px solid #000', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="method"
                      value={option.value}
                      checked={deliveryVerification.method === option.value}
                      onChange={() => handleVerificationChange({ method: option.value as 'otp' | 'id_photo' | 'collection_photo' })}
                      style={{ width: '1rem', height: '1rem' }}
                    />
                    <div>
                      <p style={{ fontWeight: 500, color: '#000' }}>{option.label}</p>
                      <p style={{ fontSize: '0.875rem', color: '#787878' }}>{option.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#000' }}>{t('config.notifications.heading')}</h2>
            <div style={{ background: 'var(--nb-bg-card)', border: '3px solid #000', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {[
                { key: 'push', label: t('config.notifications.push'), desc: t('config.notifications.pushDesc') },
                { key: 'sms', label: t('config.notifications.sms'), desc: t('config.notifications.smsDesc') },
                { key: 'whatsapp', label: t('config.notifications.whatsapp'), desc: t('config.notifications.whatsappDesc') },
              ].map((item, idx) => (
                <div key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: idx < 2 ? '1rem' : 0, borderBottom: idx < 2 ? '2px solid #000' : 'none' }}>
                  <div>
                    <p style={{ fontWeight: 500, color: '#000' }}>{item.label}</p>
                    <p style={{ fontSize: '0.875rem', color: '#787878' }}>{item.desc}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleNotificationToggle(item.key as keyof NotificationSettings)}
                    style={{
                      position: 'relative',
                      width: '2.75rem',
                      height: '1.5rem',
                      background: notifications[item.key as keyof typeof notifications] ? '#000000' : '#d1d5db',
                      border: '2px solid #000',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{
                      position: 'absolute',
                      top: '0.1rem',
                      left: notifications[item.key as keyof typeof notifications] ? '1.3rem' : '0.1rem',
                      width: '1rem',
                      height: '1rem',
                      background: 'var(--nb-bg-card)',
                      border: '2px solid #000',
                      transition: 'transform 0.15s',
                    }} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        );

      case 'language':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#000' }}>{t('config.language.heading') || 'Language'}</h2>
            <div style={{ background: 'var(--nb-bg-card)', border: '3px solid #000', padding: '1.5rem' }}>
              <p style={{ fontSize: '0.875rem', color: '#787878', marginBottom: '1rem' }}>
                {t('config.language.desc') || 'Select your preferred language'}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.75rem 1rem',
                      background: language === lang.code ? '#000000' : '#FFFFFF',
                      border: language === lang.code ? '3px solid #000' : '2px solid #000',
                      color: language === lang.code ? '#FFFFFF' : '#000',
                      cursor: 'pointer',
                      fontWeight: language === lang.code ? 600 : 400,
                      transition: 'all 0.15s',
                      width: '100%',
                      textAlign: 'left',
                    }}
                  >
                    <span>{lang.name}</span>
                    {language === lang.code && (
                      <span style={{ fontSize: '0.875rem' }}>✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'logout':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#000' }}>{t('config.logout.heading')}</h2>
            <div style={{ background: 'var(--nb-bg-page)', border: '2px solid #D64545', padding: '1.5rem' }}>
              <p style={{ color: '#4A4A4A', marginBottom: '1.5rem' }}>
                {t('config.logout.confirmText')}
              </p>
              <ButtonNB variant="primary" onClick={handleLogout} style={{ width: '100%', background: '#D64545', borderColor: '#000' }}>
                {t('config.logout.confirmButton')}
              </ButtonNB>
            </div>
          </div>
        );

    }
  };

  return (
    <>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#000' }}>{t('config.title')}</h1>
        <p style={{ color: '#787878', marginTop: '0.25rem' }}>{t('config.subtitle')}</p>
      </div>

      <div className="md:flex md:gap-8 md:items-start">
        {/* Sidebar — desktop only */}
        <nav className="hidden md:block" style={{
          width: '220px',
          flexShrink: 0,
          background: 'var(--nb-bg-card)',
          border: '3px solid #000',
          padding: '0.75rem 0',
        }}>
          {sidebarItems.map((item) => {
            const active = activeSection === item.key;
            return (
              <button
                key={item.key}
                onClick={() => navigate(`/${navLang}/admin/config?section=${item.key}`)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  width: '100%',
                  padding: '0.75rem 1rem',
                  background: active ? '#000' : 'transparent',
                  color: active ? '#fff' : '#000',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: active ? 600 : 400,
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  transition: 'background 0.15s',
                }}
              >
                <IconNB name={item.icon} size={16} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Tab bar — mobile only */}
        <div className="md:hidden w-full overflow-x-auto mb-4" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div style={{ display: 'flex', gap: '0.5rem', paddingBottom: '0.25rem' }}>
            {sidebarItems.map((item) => {
              const active = activeSection === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => navigate(`/${navLang}/admin/config?section=${item.key}`)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    padding: '0.5rem 0.875rem',
                    background: active ? '#000' : 'transparent',
                    color: active ? '#fff' : '#000',
                    border: active ? '3px solid #000' : '2px solid #000',
                    cursor: 'pointer',
                    fontWeight: active ? 600 : 400,
                    fontSize: '0.8rem',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  <IconNB name={item.icon} size={14} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="w-full md:flex-1 md:min-w-0">
          <CardNB style={{ padding: '1.5rem' }}>
            {renderSection()}
          </CardNB>
        </div>
      </div>

      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
    </>
  );
}
